import type { HubEvtName, HubEvtPayloads, HubMsgName, HubMsgPayloads } from '@runway/shared';
import { joinHub, type Room } from './colyseus';

/**
 * Live connection to the shared Colyseus 'hub' room (presence, invites, DM
 * fan-out). This module owns the socket lifecycle only — state lives in
 * state/hub.ts, which subscribes through `on()` / `onStatus()`.
 *
 * Behaviour:
 *  - `connect()` is idempotent; concurrent callers share one attempt.
 *  - Any unexpected drop (or a failed join) schedules a reconnect with
 *    exponential backoff + jitter (1s → 30s). `disconnect()` cancels that.
 *  - An auth failure (Colyseus code 525 / ServerError AUTH_FAILED) is treated
 *    as terminal: we go offline and let the session store handle the bounce.
 *  - Nothing here throws into React: every socket error is caught and surfaced
 *    through status + `lastError`.
 */

export type HubConnectionStatus = 'offline' | 'connecting' | 'online';

type Handler<K extends HubEvtName> = (payload: HubEvtPayloads[K]) => void;
type StatusHandler = (status: HubConnectionStatus, info: { attempt: number; lastError: string | null }) => void;

/** Shape of the hub room state (mirrors apps/server/src/rooms/HubState.ts): an aggregate count only, never a user list. */
type HubRoomState = { onlineCount?: number };

const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 30_000;
const AUTH_FAILED_CODE = 525;
const CONSENTED_CLOSE_CODES = new Set([1000, 4000]);

export class HubConnection {
  private room: Room<unknown, HubRoomState> | null = null;
  private status: HubConnectionStatus = 'offline';
  private stopped = true;
  private attempt = 0;
  private lastError: string | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pending: Promise<void> | null = null;
  private handlers = new Map<HubEvtName, Set<(payload: unknown) => void>>();
  private statusHandlers = new Set<StatusHandler>();

  getStatus(): HubConnectionStatus {
    return this.status;
  }
  getAttempt(): number {
    return this.attempt;
  }
  getLastError(): string | null {
    return this.lastError;
  }
  isOnline(): boolean {
    return this.status === 'online' && this.room !== null;
  }

  /** Subscribe to a server → client hub event. Returns an unsubscribe fn. */
  on<K extends HubEvtName>(event: K, handler: Handler<K>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    const wrapped = handler as (payload: unknown) => void;
    set.add(wrapped);
    return () => {
      set?.delete(wrapped);
    };
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => {
      this.statusHandlers.delete(handler);
    };
  }

  /** Join (or resume joining) the hub. Safe to call repeatedly. */
  connect(): Promise<void> {
    this.stopped = false;
    if (this.room) return Promise.resolve();
    if (this.pending) return this.pending;
    this.clearRetry();
    this.pending = this.attemptJoin().finally(() => {
      this.pending = null;
    });
    return this.pending;
  }

  /** Leave on purpose (logout / unmount). No reconnect will follow. */
  disconnect(): void {
    this.stopped = true;
    this.clearRetry();
    this.attempt = 0;
    const room = this.room;
    this.room = null;
    if (room) {
      room.removeAllListeners();
      void room.leave(true).catch(() => undefined);
    }
    this.setStatus('offline');
  }

  /** Send a client → server message. Returns false (and does nothing) when offline. */
  send<K extends HubMsgName>(type: K, payload: HubMsgPayloads[K]): boolean {
    const room = this.room;
    if (!room || this.status !== 'online') return false;
    try {
      room.send(type, payload);
      return true;
    } catch (err) {
      this.lastError = err instanceof Error ? err.message : String(err);
      return false;
    }
  }

  // ── internals ──────────────────────────────────────────────────────────

  private async attemptJoin(): Promise<void> {
    if (this.stopped) return;
    this.setStatus('connecting');
    try {
      const room = await joinHub<HubRoomState>();
      if (this.stopped) {
        // disconnect() raced the join: leave quietly.
        room.removeAllListeners();
        void room.leave(true).catch(() => undefined);
        return;
      }
      this.room = room;
      this.attempt = 0;
      this.lastError = null;
      this.wire(room);
      this.setStatus('online');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code = (err as { code?: number } | null)?.code;
      this.lastError = message;
      if (code === AUTH_FAILED_CODE || /sign in required/i.test(message)) {
        // Session is gone; the REST layer will bounce the user to /login.
        this.stopped = true;
        this.setStatus('offline');
        return;
      }
      this.scheduleRetry();
    }
  }

  private wire(room: Room<unknown, HubRoomState>): void {
    // Fan every known event out to subscribers. Colyseus lets us register a
    // wildcard so events we do not know about yet do not warn in the console.
    room.onMessage('*', (type, payload) => {
      const set = this.handlers.get(type as HubEvtName);
      if (!set) return;
      for (const h of set) {
        try {
          h(payload);
        } catch (err) {
          console.error(`[hub] handler for "${String(type)}" threw`, err);
        }
      }
    });

    // Presence arrives as an explicit `presence {online}` event on join (the
    // server sends each user *their* online friends), so nothing to seed here.

    room.onError((code, message) => {
      this.lastError = message ? `${message} (${code})` : `hub error ${code}`;
      console.warn('[hub] room error', code, message);
    });

    room.onLeave((code) => {
      if (this.room !== room) return; // already replaced / disconnected on purpose
      this.room = null;
      room.removeAllListeners();
      if (this.stopped || CONSENTED_CLOSE_CODES.has(code)) {
        this.setStatus('offline');
        if (!this.stopped) this.scheduleRetry(); // server restarted us politely; come back
        return;
      }
      this.lastError = `disconnected (${code})`;
      this.scheduleRetry();
    });
  }

  private scheduleRetry(): void {
    if (this.stopped) return;
    this.clearRetry();
    this.attempt += 1;
    const base = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.min(this.attempt - 1, 5));
    const delay = Math.round(base * (0.75 + Math.random() * 0.5));
    this.setStatus('connecting');
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.stopped || this.room) return;
      this.pending = this.attemptJoin().finally(() => {
        this.pending = null;
      });
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private setStatus(next: HubConnectionStatus): void {
    const changed = this.status !== next;
    this.status = next;
    if (!changed && next !== 'connecting') return;
    for (const h of this.statusHandlers) {
      try {
        h(next, { attempt: this.attempt, lastError: this.lastError });
      } catch (err) {
        console.error('[hub] status handler threw', err);
      }
    }
  }
}

/** One hub connection per tab. */
export const hubConnection = new HubConnection();

// Come back quickly when the tab regains network / visibility instead of
// waiting out a long backoff.
if (typeof window !== 'undefined') {
  const nudge = () => {
    if (hubConnection.getStatus() === 'connecting') void hubConnection.connect();
  };
  window.addEventListener('online', nudge);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') nudge();
  });
}
